// =============================================================================
// statsFormat — presentation metadata for the profile usage stats.
//
// The backend stores stats as an open-ended { metric: value } map (see
// userStatsService). This module is the CLIENT's view layer: it knows which
// metrics to show, in what order, with what label, and how to format the value
// (durations as "Xh Ym", everything else as a plain count). Both the self-view
// (ProfileDialog "Stats" section) and the other-user ProfileCard render from
// formatStats(), so the two stay consistent.
//
// Metrics the client doesn't recognize are simply ignored — the server can add a
// new counter without a client change, and an old client won't crash on it.
// =============================================================================

// Format a whole-second duration as a compact "Xh Ym" / "Ym" / "Xs" string.
// Used for the time-based metrics (online, idle, cam, mic).
export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

// Plain integer with thousands separators.
function formatCount(n) {
  return Number(n || 0).toLocaleString();
}

// Display order + label + formatter per known metric. `group` lets the UI cluster
// related stats (the headline "sesh" numbers vs. activity vs. presence). Anything
// not listed here is dropped from the display.
export const STAT_DEFS = [
  // The headline cannabis stats — the reason this feature exists.
  { key: 'buttonClicks', label: 'Button clicks', group: 'sesh', format: formatCount },
  { key: 'seshesStarted', label: 'Seshes started', group: 'sesh', format: formatCount },
  { key: 'seshesJoined', label: 'Seshes joined', group: 'sesh', format: formatCount },
  { key: 'hitsTaken', label: 'Hits taken', group: 'sesh', format: formatCount },
  { key: 'sesh420', label: '420s started', group: 'sesh', format: formatCount },
  { key: 'sesh350', label: '350s started', group: 'sesh', format: formatCount },
  { key: 'seshNoodle', label: 'Noodles', group: 'sesh', format: formatCount },

  // Time-based presence.
  { key: 'secondsOnline', label: 'Time online', group: 'presence', format: formatDuration },
  { key: 'secondsIdle', label: 'Time idle', group: 'presence', format: formatDuration },
  { key: 'visits', label: 'Visits', group: 'presence', format: formatCount },

  // Chat + media activity.
  { key: 'messagesSent', label: 'Messages sent', group: 'activity', format: formatCount },
  { key: 'mentionsSent', label: 'Mentions sent', group: 'activity', format: formatCount },
  { key: 'mentionsReceived', label: 'Mentions received', group: 'activity', format: formatCount },
  { key: 'secondsOnCam', label: 'Time on cam', group: 'media', format: formatDuration },
  { key: 'secondsOnMic', label: 'Time on mic', group: 'media', format: formatDuration },
  { key: 'timesWatched', label: 'Times watched', group: 'media', format: formatCount },
];

export const STAT_GROUPS = [
  { id: 'sesh', label: 'Sesh' },
  { id: 'presence', label: 'Presence' },
  { id: 'activity', label: 'Activity' },
  { id: 'media', label: 'Camera & Mic' },
];

/**
 * Turn a raw { metric: value } map into an ordered list of display rows:
 *   [{ key, label, group, value, display }]
 * Metrics absent from the map render as 0 (counters that simply haven't fired
 * yet), so a brand-new user shows a full, if zeroed, card rather than a blank.
 */
export function formatStats(raw = {}) {
  return STAT_DEFS.map((def) => {
    const value = raw[def.key] || 0;
    return {
      key: def.key,
      label: def.label,
      group: def.group,
      value,
      display: def.format(value),
    };
  });
}

/**
 * Group the formatted rows by STAT_GROUPS for sectioned rendering:
 *   [{ id, label, rows: [...] }]
 */
export function groupStats(raw = {}) {
  const rows = formatStats(raw);
  return STAT_GROUPS.map((g) => ({
    ...g,
    rows: rows.filter((r) => r.group === g.id),
  })).filter((g) => g.rows.length > 0);
}

// Member-since: format an ISO/date string as a friendly "Jun 2026". Returns null
// for a missing/invalid date so the caller can omit the line.
export function formatMemberSince(dateish) {
  if (!dateish) return null;
  const d = new Date(dateish);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}
